import { c as _c } from "react/compiler-runtime";
import type { Entity } from '@onefootprint/request-types/dashboard';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import getDecryptableDIs from '@/utils/get-decryptable-dis';
import GoogleMapsLoader from '../../../../../../../../../utils/google-maps-loader';
import AddressType from '../components/address-card/types';
import useAddressFieldsProps from './use-address-fields-props';
type Coordinates = {
  lat: number | undefined;
  lng: number | undefined;
};
export const getCoordinatesFromAddress = async (address: string): Promise<Coordinates | undefined> => {
  try {
    await GoogleMapsLoader.importLibrary('geocoding');
    const geocoder = new google.maps.Geocoder();
    const result = await geocoder.geocode({
      address
    });
    return {
      lat: result.results[0].geometry.location.lat(),
      lng: result.results[0].geometry.location.lng()
    };
  } catch (_e) {
    return undefined;
  }
};
const getAddressCoordinates = async (queryClient: ReturnType<typeof useQueryClient>, entity: Entity, type: AddressType, address: string) => {
  const getFromCache = () => queryClient.getQueryData<Coordinates>(['device-insights', entity.id, type]);
  const createInitialData = async (): Promise<Coordinates> => {
    const coordinates = await getCoordinatesFromAddress(address);
    return {
      lat: coordinates?.lat,
      lng: coordinates?.lng
    };
  };
  const possibleData = getFromCache();
  if (possibleData) {
    return possibleData;
  }
  const initialData = await createInitialData();
  return initialData;
};
const useAddressCoordinates = (entity, type) => {
  const $ = _c(29);
  const queryClient = useQueryClient();
  const {
    getAddressFieldsProps
  } = useAddressFieldsProps(entity);
  let t0;
  if ($[0] !== entity.id || $[1] !== queryClient || $[2] !== type) {
    t0 = newData => {
      queryClient.setQueryData(["device-insights", entity.id, type], prevData => ({
        ...prevData,
        lat: newData.lat,
        lng: newData.lng
      }));
    };
    $[0] = entity.id;
    $[1] = queryClient;
    $[2] = type;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const update = t0;
  let hasCompleteAddress;
  let t1;
  if ($[4] !== entity || $[5] !== getAddressFieldsProps || $[6] !== type) {
    const addressProps = getAddressFieldsProps(type);
    let t2;
    if ($[9] !== entity) {
      const decryptableDIs = getDecryptableDIs(entity);
      t2 = new Set(decryptableDIs);
      $[9] = entity;
      $[10] = t2;
    } else {
      t2 = $[10];
    }
    const decryptableSet = t2;
    const encryptedFields = addressProps.filter(_temp);
    let t3;
    if ($[11] !== decryptableSet) {
      t3 = field => !field || decryptableSet.has(field.name) && field.canDecrypt;
      $[11] = decryptableSet;
      $[12] = t3;
    } else {
      t3 = $[12];
    }
    const decryptableFields = encryptedFields.filter(t3);
    hasCompleteAddress = addressProps.length > 0 && decryptableFields.length === 0;
    t1 = addressProps.map(_temp2).filter(_temp3).join(", ");
    $[4] = entity;
    $[5] = getAddressFieldsProps;
    $[6] = type;
    $[7] = hasCompleteAddress;
    $[8] = t1;
  } else {
    hasCompleteAddress = $[7];
    t1 = $[8];
  }
  const completeAddress = t1;
  const addressTypeRequired = entity.kind === "business" ? AddressType.business : AddressType.residential;
  const enabled = !!entity && type === addressTypeRequired && hasCompleteAddress && !!completeAddress;
  let t2;
  if ($[13] !== entity.id || $[14] !== type) {
    t2 = ["device-insights", entity.id, type];
    $[13] = entity.id;
    $[14] = type;
    $[15] = t2;
  } else {
    t2 = $[15];
  }
  let t3;
  if ($[16] !== completeAddress || $[17] !== entity || $[18] !== queryClient || $[19] !== type) {
    t3 = () => getAddressCoordinates(queryClient, entity, type, completeAddress);
    $[16] = completeAddress;
    $[17] = entity;
    $[18] = queryClient;
    $[19] = type;
    $[20] = t3;
  } else {
    t3 = $[20];
  }
  let t4;
  if ($[21] !== enabled || $[22] !== t2 || $[23] !== t3) {
    t4 = {
      queryKey: t2,
      queryFn: t3,
      enabled
    };
    $[21] = enabled;
    $[22] = t2;
    $[23] = t3;
    $[24] = t4;
  } else {
    t4 = $[24];
  }
  const query = useQuery(t4);
  const t5 = query.isPending && query.fetchStatus !== "idle";
  let t6;
  if ($[25] !== query || $[26] !== t5 || $[27] !== update) {
    t6 = {
      ...query,
      update,
      isPending: t5
    };
    $[25] = query;
    $[26] = t5;
    $[27] = update;
    $[28] = t6;
  } else {
    t6 = $[28];
  }
  return t6;
};
export default useAddressCoordinates;
function _temp(prop) {
  return !prop.isDecrypted;
}
function _temp2(prop_0) {
  return prop_0.value;
}
function _temp3(v) {
  return !!v;
}